import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/components/CampaignsList.module.css";
import { useData } from "@/contexts/DataContext";

// NOTE: Legacy fields budget / validityDays not yet in backend schema.
// We display only fields that exist. Extend schema later if needed.

const CampaignsList = ({ onOpenActionsForCampaign }) => {
  const { t } = useTranslation();
  const { campaigns, actions, updateCampaign, deleteCampaign } = useData();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    budget: "",
    startsAt: "",
    endsAt: "",
  });

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "",
      description: c.description || "",
      budget: c.budget ?? "",
      startsAt: c.startsAt ? new Date(c.startsAt).toISOString().slice(0, 10) : "",
      endsAt: c.endsAt ? new Date(c.endsAt).toISOString().slice(0, 10) : "",
    });
  };
  const cancelEdit = () => {
    setEditing(null);
  };
  const save = async (id) => {
    await updateCampaign(id, {
      name: form.name,
      description: form.description || undefined,
      budget: form.budget !== "" ? parseFloat(form.budget) : undefined,
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : undefined,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
    });
    setEditing(null);
  };
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | active | expired
  const [openMenuId, setOpenMenuId] = useState(null);

  const now = new Date();
  const filtered = campaigns
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    .filter((c) => {
      if (filter === "all") return true;
      if (filter === "active") return c.active;
      if (filter === "expired") return c.endsAt ? new Date(c.endsAt) < now : false;
      return true;
    });

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('merchant.campaigns.searchPlaceholder')}
            className="border rounded px-3 py-2 flex-1 sm:w-64 sm:flex-none text-sm"
          />
        </div>
        <div className="flex items-center gap-1 text-xs overflow-x-auto">
          <button
            className={`px-3 py-2 border rounded whitespace-nowrap ${filter==='all'?'bg-blue-600 text-white':'bg-background'}`}
            onClick={() => setFilter('all')}
            type="button"
          >{t('merchant.campaigns.filters.all')}</button>
          <button
            className={`px-3 py-2 border rounded whitespace-nowrap ${filter==='active'?'bg-blue-600 text-white':'bg-background'}`}
            onClick={() => setFilter('active')}
            type="button"
          >{t('merchant.campaigns.filters.active')}</button>
          <button
            className={`px-3 py-2 border rounded whitespace-nowrap ${filter==='expired'?'bg-blue-600 text-white':'bg-background'}`}
            onClick={() => setFilter('expired')}
            type="button"
          >{t('merchant.campaigns.filters.expired')}</button>
        </div>
      </div>

  {/* Mobile Card Layout */}
  <div className={`${styles.cardsMobile} space-y-3`}>
        {filtered.map((c) => {
          const countActions = actions.filter((a) => a.campaignId === c.id).length;
          return (
            <div key={c.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  {editing === c.id ? (
                    <input
                      className="border px-2 py-1 rounded text-sm w-full"
                      value={form.name}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  ) : (
                    <h3 className="font-medium text-sm truncate">{c.name}</h3>
                  )}
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs ml-2 ${
                    c.active
                      ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {c.active ? t('merchant.campaigns.status.active') : t('merchant.campaigns.status.inactive')}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium">{t('merchant.campaigns.budget')}:</span>{" "}
                  {editing === c.id ? (
                    <input
                      type="number"
                      step="0.01"
                      className="border px-2 py-1 rounded text-xs w-full mt-1"
                      value={form.budget}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, budget: e.target.value }))
                      }
                    />
                  ) : (
                    c.budget ?? "-"
                  )}
                </div>
                <div>
                  <span className="font-medium">{t('merchant.campaigns.actions')}:</span> {countActions}
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                <span className="font-medium">{t('merchant.campaigns.period')}:</span>{" "}
                {editing === c.id ? (
                  <div className="space-y-1 mt-1">
                    <input
                      type="date"
                      className="border px-2 py-1 rounded text-xs w-full"
                      value={form.startsAt}
                      onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                    />
                    <input
                      type="date"
                      className="border px-2 py-1 rounded text-xs w-full"
                      value={form.endsAt}
                      onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                    />
                  </div>
                ) : (
                  <span>
                    {c.startsAt ? new Date(c.startsAt).toLocaleDateString() : "-"} – {c.endsAt ? new Date(c.endsAt).toLocaleDateString() : "-"}
                  </span>
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                {t('merchant.campaigns.created')}: {new Date(c.createdAt).toLocaleDateString()}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {editing === c.id ? (
                  <>
                    <button
                      onClick={() => save(c.id)}
                      className="text-xs bg-blue-600 text-white px-3 py-2 rounded flex-1 min-h-[36px]"
                    >
                      {t('merchant.campaigns.save')}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="text-xs border px-3 py-2 rounded flex-1 min-h-[36px]"
                    >
                      {t('merchant.campaigns.cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(c)}
                      className="text-xs border px-3 py-2 rounded flex-1 min-h-[36px]"
                    >
                      {t('merchant.campaigns.edit')}
                    </button>
                    <button
                      onClick={() =>
                        updateCampaign(c.id, { active: !c.active })
                      }
                      className="text-xs border px-3 py-2 rounded flex-1 min-h-[36px]"
                    >
                      {c.active ? t('merchant.campaigns.deactivate') : t('merchant.campaigns.activate')}
                    </button>
                    <button
                      onClick={() =>
                        window.confirm(t('merchant.campaigns.confirmDelete')) && deleteCampaign(c.id)
                      }
                      className="text-xs border px-3 py-2 rounded text-red-600 min-h-[36px]"
                    >
                      {t('merchant.campaigns.delete')}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="py-8 text-center text-muted-foreground text-sm">
            {t('merchant.campaigns.noneFound')}
          </div>
        )}
      </div>

  {/* Desktop Table Layout */}
  <div className={`${styles.tableDesktop} overflow-x-auto`}>
        <table className="w-full text-xs table-fixed">
          <thead>
            <tr className="text-left border-b text-[11px] text-muted-foreground bg-muted">
              <th className="py-2 px-2 w-[26%]">{t('merchant.campaigns.thead.name')}</th>
              <th className="py-2 px-2 w-[14%]">{t('merchant.campaigns.thead.status')}</th>
              <th className="py-2 px-2 w-[12%]">{t('merchant.campaigns.budget')}</th>
              <th className="py-2 px-2 w-[18%]">{t('merchant.campaigns.period')}</th>
              <th className="py-2 px-2 w-[10%]">{t('merchant.campaigns.actions')}</th>
              <th className="py-2 px-2 w-[12%]">{t('merchant.campaigns.created')}</th>
              <th className="py-2 px-2 w-[8%]" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const countActions = actions.filter(
                (a) => a.campaignId === c.id
              ).length;
              return (
                <tr key={c.id} className="border-b hover:bg-accent">
                  <td className="py-2 px-2 font-medium align-top max-w-[260px] truncate" title={c.name}>
                    {editing === c.id ? (
                      <input
                        className="border px-2 py-1 rounded text-sm"
                        value={form.name}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, name: e.target.value }))
                        }
                      />
                    ) : (
                      c.name
                    )}
                  </td>
                  <td className="py-2 px-2 align-top">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        c.active
                          ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {c.active ? t('merchant.campaigns.status.active') : t('merchant.campaigns.status.inactive')}
                    </span>
                  </td>
                  <td className="py-2 px-2 align-top text-[11px] text-foreground">
                    {editing === c.id ? (
                      <input
                        type="number"
                        step="0.01"
                        className="border px-2 py-1 rounded text-sm w-24"
                        value={form.budget}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, budget: e.target.value }))
                        }
                      />
                    ) : (
                      c.budget ?? "-"
                    )}
                  </td>
                  <td className="py-2 px-2 align-top text-[11px] text-foreground whitespace-nowrap">
                    {editing === c.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          className="border px-2 py-1 rounded text-sm"
                          value={form.startsAt}
                          onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                        />
                        <span className="text-xs">{t('merchant.campaigns.until')}</span>
                        <input
                          type="date"
                          className="border px-2 py-1 rounded text-sm"
                          value={form.endsAt}
                          onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                        />
                      </div>
                    ) : (
                      <span>
                        {c.startsAt ? new Date(c.startsAt).toLocaleDateString() : "-"} – {c.endsAt ? new Date(c.endsAt).toLocaleDateString() : "-"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 align-top text-center text-[11px] text-foreground">{countActions}</td>
                  <td className="py-2 px-2 align-top text-[11px] text-foreground whitespace-nowrap">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-2 text-right align-top">
                    {editing === c.id ? (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => save(c.id)}
                          className="text-[11px] bg-blue-600 text-white px-2 py-1 rounded"
                        >
                          {t('merchant.campaigns.save')}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-[11px] border px-2 py-1 rounded"
                        >
                          {t('merchant.campaigns.cancelShort')}
                        </button>
                      </div>
                    ) : (
                      <div className="inline-block text-left relative">
                        <button
                          type="button"
                          onClick={() => setOpenMenuId((cur) => (cur === c.id ? null : c.id))}
                          className="border px-2 py-1 rounded text-xs"
                          aria-haspopup="true"
                          aria-expanded={openMenuId === c.id}
                          title={t('merchant.campaigns.actions')}
                        >
                          <span aria-hidden>⚙️</span>
                        </button>
                        {openMenuId === c.id && (
                          <div className="absolute right-0 mt-1 w-44 bg-popover border rounded shadow z-50">
                            <button
                              className="w-full text-left px-3 py-2 text-[11px] hover:bg-accent"
                              onClick={() => { setOpenMenuId(null); startEdit(c); }}
                            >
                              {t('merchant.campaigns.edit')}
                            </button>
                            <button
                              className="w-full text-left px-3 py-2 text-[11px] hover:bg-accent"
                              onClick={() => { setOpenMenuId(null); onOpenActionsForCampaign && onOpenActionsForCampaign(c.id); }}
                            >
                              {t('merchant.campaigns.actionsForCampaign')}
                            </button>
                            <button
                              className="w-full text-left px-3 py-2 text-[11px] hover:bg-accent"
                              onClick={() => { setOpenMenuId(null); updateCampaign(c.id, { active: !c.active }); }}
                            >
                              {c.active ? t('merchant.campaigns.deactivate') : t('merchant.campaigns.activate')}
                            </button>
                            <button
                              className="w-full text-left px-3 py-2 text-[11px] hover:bg-accent text-red-600"
                              onClick={() => {
                                setOpenMenuId(null);
                                if (window.confirm(t('merchant.campaigns.confirmDelete'))) deleteCampaign(c.id);
                              }}
                            >
                              {t('merchant.campaigns.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-center text-muted-foreground text-xs">
                  {t('merchant.campaigns.none')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignsList;
